import { lockBodyScroll, unlockBodyScroll } from './body-scroll-lock';

const DEFAULT_SPECIALIST_URL = '/specialists/';
const DEFAULT_PATIENT_URL = '/patients/';
const DEFAULT_MODAL_ID = 'specialist-modal';
const MODAL_FOCUS_DELAY = 350;

const USER_ROLE_COOKIE_NAME = 'innfort-user-role';
const USER_ROLE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const USER_ROLES = {
  medicalWorker: 'medical-worker',
  patient: 'patient',
};

const getCookie = (name) => {
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');

  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }

  return '';
};

const hasUserRoleCookie = () => getCookie(USER_ROLE_COOKIE_NAME) !== '';

const normalizePathname = (pathname) => `/${String(pathname).replace(/^\/+|\/+$/g, '')}`;

const isSpecialistDirectPage = (pathname = window.location.pathname) => {
  const normalized = normalizePathname(pathname);

  return (
    normalized === '/implants' ||
    normalized === '/richter' ||
    normalized.startsWith('/implants/') ||
    normalized.startsWith('/richter/')
  );
};

const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const modalControllers = new Map();
let specialistModalTriggersInitialized = false;

const setUserRoleCookie = (userRole) => {
  const cookieAttributes = [
    `${USER_ROLE_COOKIE_NAME}=${encodeURIComponent(userRole)}`,
    'Path=/',
    `Max-Age=${USER_ROLE_COOKIE_MAX_AGE}`,
    'SameSite=Lax',
  ];

  if (window.location.protocol === 'https:') {
    cookieAttributes.push('Secure');
  }

  document.cookie = cookieAttributes.join('; ');
};

const getDestinationUrl = (trigger, destinationUrl) => {
  if (typeof destinationUrl === 'string' && destinationUrl.trim()) {
    return destinationUrl;
  }

  const anchor =
    trigger instanceof HTMLAnchorElement
      ? trigger
      : trigger instanceof Element
        ? trigger.closest('a')
        : null;

  if (anchor instanceof HTMLAnchorElement) {
    const triggerUrl = anchor.getAttribute('href');

    if (triggerUrl && triggerUrl !== '#') {
      return triggerUrl;
    }
  }

  return DEFAULT_SPECIALIST_URL;
};

const createSpecialistModalController = (modal) => {
  if (!(modal instanceof HTMLElement) || !modal.id) {
    return null;
  }

  const existingController = modalControllers.get(modal.id);

  if (existingController) {
    return existingController;
  }

  const overlay = modal.querySelector('.modal-specialist__overlay');
  const dialog = modal.querySelector('.modal-specialist__dialog');
  const roleButtons = modal.querySelectorAll('[data-specialist-role]');
  const initialFocusElement = modal.querySelector('[data-specialist-initial-focus]');

  if (
    !(overlay instanceof HTMLElement) ||
    !(dialog instanceof HTMLElement) ||
    !(initialFocusElement instanceof HTMLElement)
  ) {
    return null;
  }

  let isOpen = false;
  let focusTimerId = null;
  let lastFocusedElement = null;
  let destinationUrl = DEFAULT_SPECIALIST_URL;

  const getFocusableElements = () =>
    Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element instanceof HTMLElement && !element.hidden
    );

  const closeModal = ({ restoreFocus = true } = {}) => {
    if (!isOpen) {
      return;
    }

    isOpen = false;

    if (focusTimerId !== null) {
      window.clearTimeout(focusTimerId);
      focusTimerId = null;
    }

    modal.classList.remove('modal-specialist--open');
    modal.setAttribute('aria-hidden', 'true');

    unlockBodyScroll();

    if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  };

  const openModal = ({ trigger = null, destinationUrl: nextDestinationUrl = null } = {}) => {
    if (isOpen || hasUserRoleCookie()) {
      return;
    }

    destinationUrl = getDestinationUrl(trigger, nextDestinationUrl);

    lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;

    isOpen = true;

    modal.classList.add('modal-specialist--open');
    modal.setAttribute('aria-hidden', 'false');

    lockBodyScroll();

    focusTimerId = window.setTimeout(() => {
      focusTimerId = null;
      initialFocusElement.focus();
    }, MODAL_FOCUS_DELAY);
  };

  roleButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const userRole = button.dataset.specialistRole;

      if (!Object.values(USER_ROLES).includes(userRole)) {
        return;
      }

      setUserRoleCookie(userRole);
      closeModal({ restoreFocus: false });

      if (userRole === USER_ROLES.patient) {
        window.location.assign(DEFAULT_PATIENT_URL);
        return;
      }

      window.location.assign(destinationUrl);
    });
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();

      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements();

    if (!focusableElements.length) {
      event.preventDefault();

      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!dialog.contains(document.activeElement)) {
      event.preventDefault();

      (event.shiftKey ? lastElement : firstElement).focus();

      return;
    }

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });

  const controller = {
    open: openModal,
  };

  modalControllers.set(modal.id, controller);

  return controller;
};

export const openSpecialistModal = (modalId = DEFAULT_MODAL_ID, options = {}) => {
  if (hasUserRoleCookie()) {
    return;
  }

  const controller = createSpecialistModalController(document.getElementById(modalId));

  controller?.open(options);
};

export const initSpecialistModalTriggers = () => {
  if (specialistModalTriggersInitialized) {
    return;
  }

  specialistModalTriggersInitialized = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-specialist-modal-open]')
      : null;

    if (!trigger) {
      return;
    }

    const roleCookie = getCookie(USER_ROLE_COOKIE_NAME);

    if (roleCookie === USER_ROLES.patient) {
      event.preventDefault();
      window.location.assign(DEFAULT_PATIENT_URL);
      return;
    }

    if (roleCookie === USER_ROLES.medicalWorker) {
      return;
    }

    if (roleCookie) {
      return;
    }

    event.preventDefault();

    openSpecialistModal(DEFAULT_MODAL_ID, {
      trigger,
      destinationUrl: getDestinationUrl(trigger),
    });
  });
};

export const initSpecialistDirectPage = () => {
  if (!isSpecialistDirectPage()) {
    return;
  }

  const roleCookie = getCookie(USER_ROLE_COOKIE_NAME);

  if (roleCookie === USER_ROLES.patient) {
    window.location.replace(DEFAULT_PATIENT_URL);
    return;
  }

  if (roleCookie) {
    return;
  }

  const destinationUrl = `${window.location.pathname}${window.location.search}`;

  openSpecialistModal(DEFAULT_MODAL_ID, {
    destinationUrl: destinationUrl || '/implants/',
  });
};

export const initSpecialistModal = (modalId = DEFAULT_MODAL_ID) => {
  if (window.__innfortUserRoleGate) {
    return null;
  }

  const controller = createSpecialistModalController(document.getElementById(modalId));

  initSpecialistModalTriggers();
  initSpecialistDirectPage();

  return controller;
};
